import { decodeHTMLEntities } from '@virginexperiencedays/components-v2/src/utils/decodeHTMLEntities';

import type { Product, Location } from '@virginexperiencedays/products';

export type AdditionalDetailItem = {
  name: string;
  content: string | string[];
  locations?: Location[];
};
export type AdditionalDetails = {
  items: AdditionalDetailItem[];
  accordionDefaultOpen?: boolean;
};

const categoriesWithAccordionDefaultOpen = ['gift-cards'];

export const additionalDetailsExtractor = (
  product: Product,
  isCollectionChoice?: boolean
): AdditionalDetails => {
  const texts = formatTexts(product?.info?.texts);
  const whatsIncluded = formatWhatsIncluded(product?.info?.whatsIncluded);
  const location = getDetailConfig('location_map')?.title;
  const ymal = getDetailConfig('you_may_also_like')?.title;

  const items = [
    ...texts,
    ...(whatsIncluded ? [whatsIncluded] : []),
    // Details with special contents included in the ordering
    // (TODO: content of these items can then be removed once piped in AdditionalDetailsWidget)
    ...[
      { name: location, content: 'Placeholder' },
      { name: ymal, content: 'Placeholder' },
    ],
  ];

  const detailItems = isCollectionChoice
    ? COLLECTION_CHOICE_DETAILS_ORDERED
    : PRODUCT_DETAILS_ORDERED;

  const orderedItems = detailItems
    .map((k) => getDetailConfig(k))
    .map(({ title }) => getItem(title, items))
    // ensure all ordered details have content, except location and yaml
    .filter((item) => hasContent(item, location, ymal));

  return {
    accordionDefaultOpen: categoriesWithAccordionDefaultOpen.some((category) =>
      product?.categories?.includes(category)
    ),
    items: orderedItems,
  };
};

const formatTexts = (items: AdditionalDetailItem[]): AdditionalDetailItem[] => {
  if (!items?.length) return [];

  // ensure all items have name and content
  return items.filter((item) => !!item?.name && !!item?.content);
};

// Format the content by stripping tags and splitting into a list
const formatWhatsIncluded = (content: string): AdditionalDetailItem => {
  if (!content) return;

  if (!content.includes('<ul>') || !content.includes('<li>')) {
    console.error('Missing li in whatsincluded list');
    return;
  }

  // Sanitize and format the content
  const formattedList = decodeHTMLEntities(content)
    // Remove all HTML tags
    .replace(/<[^>]*>/g, '')
    // Split the content by new lines
    .split('\n')
    // Remove any empty or whitespace-only strings
    .filter((line) => line.trim().length > 0);

  return {
    name: "What's Included",
    content: formattedList,
  };
};

/**
 * Retrieves an item from the given array of AdditionalDetailItem objects based on the detail title.
 * @param detailTitle - The title of the detail to search for.
 * @param items - The array of AdditionalDetailItem objects to search in.
 * @returns The first AdditionalDetailItem object that matches the detail title, or undefined if no match is found.
 */
const getItem = (detailTitle: string, items: AdditionalDetailItem[]) =>
  items.find(({ name }) => name.toLowerCase() === detailTitle.toLowerCase());

/**
 * Checks if the given AdditionalDetailItem has content or if its name matches the provided location or ymal.
 * @param item - The AdditionalDetailItem to check.
 * @param location - The location to match against the item's name.
 * @param ymal - The ymal to match against the item's name.
 * @returns True if the item has content or if its name matches the location or ymal, false otherwise.
 */
const hasContent = (item: AdditionalDetailItem, location?: string, ymal?: string) =>
  !!item?.content || [location, ymal].includes(item?.name);

type DetailKey =
  | 'whats_included'
  | 'what_happens_on_the_day'
  | 'location_map'
  | 'you_may_also_like'
  | 'participant_guidelines'
  | 'availability'
  | 'insurance'
  | 'how_it_works'
  | 'duration'
  | 'spectators'
  | 'additional_information'
  | 'packaging_and_delivery'
  | 'delivery_guidelines'
  | 'medical_guidelines'
  | 'how_to_reserve'
  | 'dress_code'
  | 'event_info'
  | 'essential_info'
  | 'locations';

type Detail = {
  // identifier for dev
  key: DetailKey;
  // field name/title mapped from VED Admin
  title: string;
  // default state when opened in an accordion
  defaultOpen: boolean;
};

const DETAILS: Detail[] = [
  { key: 'whats_included', title: "What's Included", defaultOpen: true },
  { key: 'what_happens_on_the_day', title: 'What happens on the day?', defaultOpen: true },
  { key: 'location_map', title: 'Location', defaultOpen: true },
  { key: 'you_may_also_like', title: 'You May Also Like', defaultOpen: false },
  { key: 'participant_guidelines', title: 'Participant guidelines', defaultOpen: false },
  { key: 'availability', title: 'Availability', defaultOpen: false },
  { key: 'insurance', title: 'What about insurance?', defaultOpen: false },
  { key: 'how_it_works', title: 'How it works', defaultOpen: false },
  { key: 'duration', title: 'How long should I allow for this experience?', defaultOpen: false },
  { key: 'spectators', title: 'Can I bring spectators?', defaultOpen: false },
  { key: 'additional_information', title: 'Additional information', defaultOpen: false },
  { key: 'packaging_and_delivery', title: 'Packaging and Delivery', defaultOpen: false },
  { key: 'delivery_guidelines', title: 'Delivery Guidelines', defaultOpen: false },
  { key: 'medical_guidelines', title: 'Medical guidelines', defaultOpen: false },
  { key: 'how_to_reserve', title: 'How do I make my reservation?', defaultOpen: false },
  { key: 'dress_code', title: 'Dress code', defaultOpen: false },
  { key: 'event_info', title: 'Event information', defaultOpen: false },
  { key: 'essential_info', title: 'Essential Information', defaultOpen: false },
  { key: 'locations', title: 'What Locations are there?', defaultOpen: false },
];

// recognized details to show in the following order
export const PRODUCT_DETAILS_ORDERED: DetailKey[] = [
  'whats_included',
  'what_happens_on_the_day',
  'location_map',
  'you_may_also_like',
  'participant_guidelines',
  'availability',
  'insurance',
  'how_it_works',
  'duration',
  'spectators',
  'additional_information',
  'packaging_and_delivery',
  'delivery_guidelines',
  'medical_guidelines',
  'how_to_reserve',
  'dress_code',
  'event_info',
  'essential_info',
  'locations',
];

export const COLLECTION_CHOICE_DETAILS_ORDERED: DetailKey[] = [
  'whats_included',
  'availability',
  'location_map',
];

export const getDetailConfig = (value: string, prop: 'key' | 'title' = 'key') =>
  DETAILS.find((item) => item?.[prop]?.toLowerCase() === value?.toLowerCase());
